import React, { useEffect, useMemo } from "react";
import {
  Link,
  NavLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "./index.css";
import Observer from "@researchgate/react-intersection-observer";
import {
  AddIcon,
  ArrowBackIcon,
  EyeIcon,
  LocationIcon,
} from "../../components/icons";
import { JobTile, ListingTile } from "../../components";
import { useGetCategoryQuery } from "../../features/supabase/usecase/categories";
import {
  useGetClassifiedByCategoryQuery,
  useViewImpressionClassifiedMutation,
} from "../../features/supabase/usecase/classifieds";
import storageService from "../../app/services/storageService";
import ListingDetailPage from "../ListingDetailPage";
import { ListingTileSkeleton } from "../../components/Skeleton";
import { debounce } from "../../lib";
import { useGetLocationsQuery } from "../../features/supabase/usecase/locations";
import JobTileSkeleton from "../../components/Skeleton/JobTileSkeleton";
import { ListingFlag } from "../../components/Tiles/ListingTile";

const CategoriesPage = () => {
  // get the category from the url
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setParams] = useSearchParams();
  const [page, setPage] = React.useState(1);

  const [open, setOpen] = React.useState(false);

  const [viewImpressionClassified] = useViewImpressionClassifiedMutation();

  const {
    data: locations,
    error: locationsError,
    isLoading: locationsLoading,
  } = useGetLocationsQuery({ categoryId: parseInt(params.categoryId) });

  const [filter, setFilter] = React.useState("");
  const [search, setSearch] = React.useState({
    filter: "",
    location_id: parseInt(searchParams.get("location_id")) || -1,
  });

  const [selectedLocation, setSelectedLocation] = React.useState(
    parseInt(searchParams.get("location_id")) || -1
  );

  const [data, setData] = React.useState([]);
  const {
    data: classifiedResult,
    classifiedError,
    isLoading: isClassifiedLoading,
    isFetching,
  } = useGetClassifiedByCategoryQuery({
    categoryId: parseInt(params.categoryId),
    page: page,
    search,
  });

  useEffect(() => {
    if (searchParams.get("pg")) {
      setPage(parseInt(searchParams.get("pg")));
    }
  }, [searchParams.get("pg")]);

  function processSalary(salary) {
    if (!salary) {
      return null;
    }
    // Trim whitespace and remove '$' from the start if present
    let cleanSalary = salary.trim();
    if (cleanSalary.startsWith("$")) {
      cleanSalary = cleanSalary.substring(1).trim();
    }

    // Check if the string contains any digits
    if (/\d/.test(cleanSalary)) {
      // Check if the string is fully numeric (optionally including commas or periods)
      if (/^\d+(\.\d{1,2})?$/.test(cleanSalary.replace(/,/g, ""))) {
        // If fully numeric, format with commas every three digits
        return parseFloat(cleanSalary.replace(/,/g, "")).toLocaleString();
      }
      // Return the string as is if it contains any numbers but is not fully numeric
      return cleanSalary;
    }

    // Return empty if no numbers are present
    return null;
  }

  useEffect(() => {
    debounce(async () => {
      setSearch({ filter: filter, location_id: selectedLocation });
      // set page to 1 and location to location_id
      const params = new URLSearchParams(searchParams);
      params.set("pg", 1);
      if (selectedLocation == -1) params.delete("location_id");
      else params.set("location_id", selectedLocation);
      setParams(params);
    }, 800);
  }, [filter, selectedLocation]);

  const {
    data: result,
    error,
    isLoading,
  } = useGetCategoryQuery(parseInt(params.categoryId), {
    skip: !params.categoryId,
  });

  const handleBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <div className="categories-container">
      <div className="categories-action-buttons">
        <button className="back-button" onClick={handleBack}>
          <ArrowBackIcon size={19} fill={"black"} />
          <div>Back</div>
        </button>
        <NavLink to="/submit" className="submit-button">
          Submit a Classified
        </NavLink>
      </div>
      {isLoading ? (
        <div className="categories-title-skeleton skeleton" />
      ) : (
        <div className="categories-title">{result.name}</div>
      )}
      <div className="filters">
        <input
          type="text"
          placeholder="Search"
          onChange={(e) => setFilter(e.target.value)}
          className="categories-search-input"
        />
        <div className="location-dropdown">
          <LocationIcon size={20} fill={"black"} />
          <select
            onChange={(e) => setSelectedLocation(e.target.value)}
            value={selectedLocation}
          >
            <option value={-1}>Filter by Location</option>
            {!locationsLoading &&
              locations.map((e, i) => {
                return <option value={e.id}>{e.location}</option>;
              })}
          </select>
        </div>
      </div>

      {isLoading || isClassifiedLoading || isFetching ? (
        result?.type === "job" ? (
          <div className="categories-listings list">
            {Array(9)
              .fill(0)
              .map((_, i) => (
                <JobTileSkeleton key={i} fill />
              ))}
          </div>
        ) : (
          <div className="categories-listings">
            {Array(9)
              .fill(0)
              .map((_, i) => (
                <ListingTileSkeleton key={i} />
              ))}
          </div>
        )
      ) : result.type !== "job" ? (
        <div className="categories-listings">
          {classifiedResult.classified?.map((classified, i) => {
            return (
              <ListingTile
                key={i}
                type={classified.featured ? "featured" : ""}
                images={classified.properties?.images}
                title={classified.title}
                price={classified?.properties?.price}
                facilities={classified.facilities}
                sqft={classified?.properties?.location_sqft}
                id={classified.id}
                rate={classified?.properties?.price_type}
                onIntersection={(event, unobserve) => {
                  if (event.isIntersecting) {
                    viewImpressionClassified(classified.id);
                    unobserve();
                  }
                }}
              />
            );
          })}
        </div>
      ) : (
        <div className="categories-listings list">
          {classifiedResult.classified.map((classified, i) => {
            return (
              <JobTile
                key={i}
                type={classified.featured ? "featured" : ""}
                location={classified?.locations?.location}
                price={processSalary(classified.properties?.salary)}
                title={classified.title}
                description={classified.description}
                postedOn={classified.created_at}
                id={classified.id}
                className="job-tile mobile"
                onIntersection={(event, unobserve) => {
                  if (event.isIntersecting) {
                    viewImpressionClassified(classified.id);
                    unobserve();
                  }
                }}
              />
            );
          })}
          {classifiedResult.classified.map((classified, i) => {
            const type = classified.featured ? "featured" : "";
            return (
              <Observer
                onChange={(event, unobserve) => {
                  if (event.isIntersecting) {
                    viewImpressionClassified(classified.id);
                    unobserve();
                  }
                }}
                threshold={1}
              >
                <Link
                  key={i}
                  to={"/job/" + classified.id}
                  className={"job-container " + type}
                >
                  {type ? (
                    <ListingFlag type={type}>
                      {type === "featured" ? "FEATURED" : "NEWLY POSTED"}
                    </ListingFlag>
                  ) : undefined}
                  {classified.properties?.images &&
                    classified.properties?.images.length > 0 && (
                      <img
                        security="restricted"
                        src={`${storageService.publicUrl(
                          classified.properties.images[0]
                        )}`}
                        alt="job"
                      />
                    )}
                  <div className="job-details">
                    <div className="job-details-row">
                      <div className="job-title">{classified.title}</div>
                      <div className="job-type">
                        {classified.locations?.location}
                      </div>
                    </div>
                    <div className="job-salary">
                      {classified.properties?.salary &&
                        processSalary(classified.properties?.salary) &&
                        `$${processSalary(classified.properties?.salary)}`}
                    </div>
                    <div
                      className="job-description"
                      dangerouslySetInnerHTML={{
                        __html: classified.description,
                      }}
                    ></div>
                    <div className="job-row">
                      <div className="job-info">
                        <div className="job-posted-at">
                          Posted on{" "}
                          {new Date(classified.created_at).toDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </Observer>
            );
          })}
        </div>
      )}

      <div className="pagination mobile">
        {page > 1 && (
          <div
            className="pg"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set("pg", page - 1);
              setParams(params);
            }}
          >
            Prev
          </div>
        )}
        {(() => {
          const totalPages = Math.ceil((classifiedResult?.count || 0) / 42);
          const maxPagesToShow = 6;
          let startPage = Math.max(0, page - 3);
          let endPage = Math.min(totalPages, startPage + maxPagesToShow);

          if (endPage - startPage < maxPagesToShow) {
            startPage = Math.max(0, endPage - maxPagesToShow);
          }

          const pages = Array(totalPages)
            .fill(0)
            .map((_, i) => i + 1)
            .slice(startPage, endPage);

          return pages.map((pageNum) => (
            <div
              key={pageNum}
              className={`page ${page === pageNum ? "active" : ""}`}
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set("pg", pageNum);
                setParams(params);
              }}
            >
              {pageNum}
            </div>
          ));
        })()}

        <div
          onClick={() => {
            setOpen((val) => !val);
          }}
          className={"floating-button " + (open ? "open" : "")}
        >
          <AddIcon size={40} stroke={"#fff"} />
        </div>
        {open && (
          <div
            style={{ position: "fixed", bottom: 90, right: 20, zIndex: 1000 }}
          >
            <Link
              to="/submit"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "var(--lightRed)",
                borderRadius: "10px",
                color: "#fff",
                fontSize: 20,
                padding: "10px 20px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                textDecoration: "none",
              }}
            >
              Post Classified
            </Link>
          </div>
        )}
        {page < Math.ceil((classifiedResult?.count || 0) / 42) && (
          <div
            className="pg"
            onClick={() => {
              const params = new URLSearchParams(searchParams);
              params.set("pg", page + 1);
              setParams(params);
            }}
          >
            Next
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;
